// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SubscriptionExpirationBanner } from '@/account/components/subscription-expiration-banner';
import { SubscriptionExpirationDialog } from '@/account/components/subscription-expiration-dialog';
import { useSubscriptionNoticeAcknowledgement } from '@/account/hooks/use-subscription-notice-acknowledgement';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { NavigationLayout } from '@/components/navigation-layout';
import { PropsWithChildren } from 'react';

/**
 * The application layout during the grace period: the banner sits above the
 * navigation bar and the acknowledgement dialog stays mounted alongside the
 * routed page.
 */
export function SubscriptionExpirationNotice(props: PropsWithChildren) {
  const { user } = useCurrentUser();
  const acknowledgement = useSubscriptionNoticeAcknowledgement();
  const { subscriptionEndDate, accessEndDate } = user.organization;

  // The backend only reports grace once a subscription end date is recorded,
  // so a response without one has nothing to announce
  if (subscriptionEndDate === null) {
    return <NavigationLayout>{props.children}</NavigationLayout>;
  }

  return (
    <NavigationLayout
      banner={
        <SubscriptionExpirationBanner
          organizationName={user.organization.name}
          subscriptionEndDate={subscriptionEndDate}
          accessEndDate={accessEndDate}
          contractContact={user.organization.contractContact}
          billingContact={user.organization.billingContact}
        />
      }
    >
      {props.children}
      <SubscriptionExpirationDialog
        isOpen={acknowledgement.isOpen}
        onClose={acknowledgement.acknowledge}
        organizationName={user.organization.name}
        subscriptionEndDate={subscriptionEndDate}
        accessEndDate={accessEndDate}
        contractContact={user.organization.contractContact}
        billingContact={user.organization.billingContact}
      />
    </NavigationLayout>
  );
}
